/**
 * 记忆编辑模态框组件
 *
 * 支持创建和编辑用户记忆/工作区记忆
 * 创建时可选择记忆类型，编辑时仅修改内容
 */

import React, { useState, useEffect } from 'react';
import type {
  UserMemoryType,
  WorkspaceMemoryType,
  UserMemoryTypeLabel,
  WorkspaceMemoryTypeLabel,
  UserMemory,
  WorkspaceMemory,
} from '../../types/memory';
import {
  USER_MEMORY_TYPE_LABELS,
  WORKSPACE_MEMORY_TYPE_LABELS,
  getUserMemoryTypeLabel,
  getWorkspaceMemoryTypeLabel,
} from '../../types/memory';

interface MemoryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (memoryType: UserMemoryType | WorkspaceMemoryType, content: string) => Promise<void>;
  mode: 'create' | 'edit';
  memoryScope: 'user' | 'workspace';
  initialMemory?: UserMemory | WorkspaceMemory;
}

export const MemoryEditModal: React.FC<MemoryEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  memoryScope,
  initialMemory,
}) => {
  const [content, setContent] = useState('');
  const [memoryType, setMemoryType] = useState<UserMemoryType | WorkspaceMemoryType>(
    memoryScope === 'user' ? 'preference' : 'writing_preference'
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialMemory) {
        setContent(initialMemory.content);
        setMemoryType(initialMemory.memory_type);
      } else {
        setContent('');
        setMemoryType(memoryScope === 'user' ? 'preference' : 'writing_preference');
      }
      setError('');
    }
  }, [isOpen, mode, initialMemory, memoryScope]);

  if (!isOpen) return null;

  const typeLabels: UserMemoryTypeLabel[] | WorkspaceMemoryTypeLabel[] =
    memoryScope === 'user' ? USER_MEMORY_TYPE_LABELS : WORKSPACE_MEMORY_TYPE_LABELS;

  const handleSave = async () => {
    if (!content.trim()) {
      setError('请输入记忆内容');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await onSave(memoryType, content.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const getTypeLabel = (type: UserMemoryType | WorkspaceMemoryType) => {
    if (memoryScope === 'user') {
      return getUserMemoryTypeLabel(type as UserMemoryType);
    }
    return getWorkspaceMemoryTypeLabel(type as WorkspaceMemoryType);
  };

  const selectedTypeLabel = getTypeLabel(memoryType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? '新增记忆' : '编辑记忆'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                记忆类型
              </label>
              <div className="grid grid-cols-2 gap-2">
                {typeLabels.map((label) => (
                  <button
                    key={label.type}
                    type="button"
                    onClick={() => setMemoryType(label.type)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      memoryType === label.type
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {selectedTypeLabel.description}
              </p>
            </div>
          )}

          {mode === 'edit' && initialMemory && (
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${selectedTypeLabel.color}`}>
                {selectedTypeLabel.label}
              </span>
              <span className="text-xs text-gray-500">
                类型不可修改
              </span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              记忆内容
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="描述你希望AI记住的内容..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              {content.length} / 1000 字符
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !content.trim()}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemoryEditModal;